import { Reliability } from "@carbon/pictograms-react";
import { Heading, Section, Stack } from "@carbon/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { any, object, record } from "zod";

import { ConfirmActionModal, PassiveModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError, useCreateError } from "src/errors";
import { useExecuteScenarioMutation } from "src/graphql/anti-fraud/queries/scenarios/ExecuteScenarioMutation.ts";
import { useGetShortScenariosQuery } from "src/graphql/anti-fraud/queries/scenarios/GetShortScenariosQuery.ts";
import { useT } from "src/internationalization";

import { ScenarioFilterableSinglePickerFormField } from "./ScenarioFilterableSinglePickerFormField.tsx";

export type ExecuteScenarioModalProps = {
  onClose: () => void;
  selectedCase: {
    id: string;
    request: string | null;
    typeCode: string;
  };
  open: boolean;
  showLink: boolean;
  beforeSubmit?: () => void;
};

type FormData = {
  scenarioVersion: string | null;
};

export const ExecuteScenarioModal: React.FC<ExecuteScenarioModalProps> = ({
  onClose,
  selectedCase,
  open,
  beforeSubmit,
  showLink,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const createError = useCreateError();

  const validationSchema = useSchema();

  const methods = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      scenarioVersion: null,
    },
  });

  const { handleSubmit, reset, setError, control } = methods;

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const { performMutation, loading, error } = useExecuteScenarioMutation({
    onSuccess: (data) => {
      showSuccessToast(t("page.cases.tab.requests.modal.execute.success.message"));
      onClose();
      if (selectedCase?.id && data?.executeScenario.scenarioExecutionResult.scenarioVersion?.id) {
        const searchParams = new URLSearchParams({
          executionId: data?.executeScenario.scenarioExecutionResult.id ?? "",
        });

        navigate(
          {
            pathname: generatePath("/case/:caseTypeCode/:id/ExecutionsDashboard", {
              caseTypeCode: selectedCase.typeCode,
              id: selectedCase?.id ?? "",
            }),
            search: searchParams.toString(),
          },
          {
            replace: false,
          }
        );
      }
    },
  });

  const onSubmit = async (data) => {
    beforeSubmit && beforeSubmit();
    if (selectedCase) {
      await performMutation({
        caseId: selectedCase.id,
        scenarioId: data.scenarioVersion.id,
      });
    }
  };

  const {
    data: scenarios,
    loading: scenariosLoading,
    error: scenariosError,
  } = useGetShortScenariosQuery({
    variables: {
      caseTypeCodes: [selectedCase.typeCode],
    },
    onError: () => {
      setError("scenarioVersion", {
        message: createError("FETCH-SCENARIOS-ERROR", null).message,
      });
    },
  });

  const isEmpty = !scenariosLoading && scenarios.length === 0;

  if (isEmpty && !scenariosError) {
    return (
      <PassiveModal onClose={onClose} title={t("page.cases.tab.requests.modal.execute.title")}>
        <EmptyView caseId={selectedCase.id} showLink={showLink} />
      </PassiveModal>
    );
  }

  return (
    <ConfirmActionModal
      onClose={onClose}
      loading={loading}
      error={mapToModalError(error)}
      title={t("page.cases.tab.requests.modal.execute.title")}
      subtitle={t("page.cases.tab.requests.modal.execute.subtitle", {
        request: selectedCase.request,
      })}
      onConfirm={handleSubmit(onSubmit)}
      size="md"
      confirmButtonText={t("page.cases.tab.requests.modal.execute.button.primary")}
      initialFocus="#scenarioVersion"
    >
      <Stack gap={5}>
        <ScenarioFilterableSinglePickerFormField
          name="scenarioVersion"
          label={t("page.cases.tab.requests.modal.execute.form.scenario.title")}
          helperText={t("page.cases.tab.requests.modal.execute.form.scenario.helper")}
          items={scenarios}
          loading={scenariosLoading}
          control={control}
          id="scenarioVersion"
          noOptionsText={t("page.cases.tab.requests.modal.execute.empty.scenarios.search.title")}
        />
      </Stack>
    </ConfirmActionModal>
  );
};

const EmptyView: FC<{ caseId: string; showLink: boolean }> = ({ caseId, showLink }) => {
  const t = useT();

  return (
    <div className="text-center">
      <Reliability />
      <Section level={4}>
        <Heading>{t("page.cases.tab.requests.modal.execute.empty.scenarios.title")}</Heading>
      </Section>
      <p className="text-textSecondary">
        {t("page.cases.tab.requests.modal.execute.empty.scenarios.description")}
      </p>
      {showLink && (
        <Link to={`/case/${caseId}/ExecutionsDashboard`} className="no-underline">
          {t("page.cases.tab.requests.modal.execute.empty.scenarios.link")}
        </Link>
      )}
    </div>
  );
};

const useSchema = () => {
  const t = useT();

  return useMemo(
    () =>
      object({
        scenarioVersion: record(any(), {
          message: t("page.cases.tab.requests.modal.execute.form.scenario.invalid"),
        }),
      }),
    [t]
  );
};
